import { useContext, useState } from "react";
import classNames from "classnames";

import { MapContext } from "../../../../Map";
import KeyboardButton from "../KeyboardButton/KeyboardButton";
import styles from "./ElGRKeyboard.module.scss";
import { KeyboardType } from "../../Keyboard";

interface Props {
  handleClick: (...args: any[]) => void;
  handleBackspace: (...args: any[]) => void;
  handleKeyboardTypeSwitch?: (type: KeyboardType) => void;
  handleSearch: () => void;
  languageSwitchButton?: React.ReactNode;
  showSearchKey?: boolean;
}

// Greek accent mappings - which characters can receive accents
const accentMappings: Record<string, string> = {
  α: "ά",
  ε: "έ",
  η: "ή",
  ι: "ί",
  ο: "ό",
  υ: "ύ",
  ω: "ώ",
};

// Characters that can receive accents
const accentableChars = new Set(Object.keys(accentMappings));

const ElGRKeyboard: React.FC<Props> = ({
  handleClick,
  handleBackspace,
  handleKeyboardTypeSwitch,
  handleSearch,
  languageSwitchButton,
  showSearchKey = true,
}) => {
  const { controlTheme } = useContext(MapContext);
  const [accentMode, setAccentMode] = useState(false);

  const handleAccentClick = () => {
    setAccentMode(!accentMode);
  };

  const handleCharacterClick = (char: string) => {
    if (accentMode && accentMappings[char]) {
      // Send the accented version
      handleClick({ currentTarget: { name: accentMappings[char] } });
      // Exit accent mode after typing an accented character
      setAccentMode(false);
    } else {
      // Normal character click
      handleClick({ currentTarget: { name: char } });
      // If we're in accent mode and this char can't be accented, exit accent mode
      if (accentMode) {
        setAccentMode(false);
      }
    }
  };

  const getButtonText = (originalChar: string): string => {
    if (accentMode && accentMappings[originalChar]) {
      return accentMappings[originalChar];
    }
    return originalChar;
  };

  const isCharacterDisabled = (char: string): boolean => {
    return accentMode && !accentableChars.has(char);
  };

  return (
    <div className={classNames(styles.container, styles[controlTheme])}>
      <div className={styles.backdrop} />
      <div className={classNames(styles.row, styles.rowOne)}>
        <KeyboardButton
          type="text"
          text={getButtonText("ε")}
          onClick={() => handleCharacterClick("ε")}
          disabled={isCharacterDisabled("ε")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("ρ")}
          onClick={() => handleCharacterClick("ρ")}
          disabled={isCharacterDisabled("ρ")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("τ")}
          onClick={() => handleCharacterClick("τ")}
          disabled={isCharacterDisabled("τ")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("υ")}
          onClick={() => handleCharacterClick("υ")}
          disabled={isCharacterDisabled("υ")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("θ")}
          onClick={() => handleCharacterClick("θ")}
          disabled={isCharacterDisabled("θ")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("ι")}
          onClick={() => handleCharacterClick("ι")}
          disabled={isCharacterDisabled("ι")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("ο")}
          onClick={() => handleCharacterClick("ο")}
          disabled={isCharacterDisabled("ο")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("π")}
          onClick={() => handleCharacterClick("π")}
          disabled={isCharacterDisabled("π")}
        />
        <KeyboardButton
          type="text"
          text="΄"
          onClick={handleAccentClick}
          className={accentMode ? styles.accentModeActive : undefined}
        />
      </div>

      <div className={classNames(styles.row, styles.rowTwo)}>
        <KeyboardButton
          type="text"
          text={getButtonText("α")}
          onClick={() => handleCharacterClick("α")}
          disabled={isCharacterDisabled("α")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("σ")}
          onClick={() => handleCharacterClick("σ")}
          disabled={isCharacterDisabled("σ")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("δ")}
          onClick={() => handleCharacterClick("δ")}
          disabled={isCharacterDisabled("δ")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("φ")}
          onClick={() => handleCharacterClick("φ")}
          disabled={isCharacterDisabled("φ")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("γ")}
          onClick={() => handleCharacterClick("γ")}
          disabled={isCharacterDisabled("γ")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("η")}
          onClick={() => handleCharacterClick("η")}
          disabled={isCharacterDisabled("η")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("ξ")}
          onClick={() => handleCharacterClick("ξ")}
          disabled={isCharacterDisabled("ξ")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("κ")}
          onClick={() => handleCharacterClick("κ")}
          disabled={isCharacterDisabled("κ")}
        />
        <KeyboardButton
          type="text"
          text={getButtonText("λ")}
          onClick={() => handleCharacterClick("λ")}
          disabled={isCharacterDisabled("λ")}
        />
      </div>

      <div className={classNames(styles.row, styles.rowThree)}>
        <div className={styles.rowThreeKeys}>
          <KeyboardButton
            type="text"
            text={getButtonText("ζ")}
            onClick={() => handleCharacterClick("ζ")}
            disabled={isCharacterDisabled("ζ")}
          />
          <KeyboardButton
            type="text"
            text={getButtonText("χ")}
            onClick={() => handleCharacterClick("χ")}
            disabled={isCharacterDisabled("χ")}
          />
          <KeyboardButton
            type="text"
            text={getButtonText("ψ")}
            onClick={() => handleCharacterClick("ψ")}
            disabled={isCharacterDisabled("ψ")}
          />
          <KeyboardButton
            type="text"
            text={getButtonText("ω")}
            onClick={() => handleCharacterClick("ω")}
            disabled={isCharacterDisabled("ω")}
          />
          <KeyboardButton
            type="text"
            text={getButtonText("β")}
            onClick={() => handleCharacterClick("β")}
            disabled={isCharacterDisabled("β")}
          />
          <KeyboardButton
            type="text"
            text={getButtonText("ν")}
            onClick={() => handleCharacterClick("ν")}
            disabled={isCharacterDisabled("ν")}
          />
          <KeyboardButton
            type="text"
            text={getButtonText("μ")}
            onClick={() => handleCharacterClick("μ")}
            disabled={isCharacterDisabled("μ")}
          />
        </div>
        <KeyboardButton
          dataQA="backspace"
          type="icon"
          icon="backspace"
          value=""
          onClick={handleBackspace}
          className={styles.backspaceKey}
        />
      </div>
      <div className={classNames(styles.row, styles.rowFour)}>
        <div className={classNames(styles.column, styles.columnOne)}>
          <KeyboardButton
            dataQA="numeric-switch"
            type="text"
            text="123"
            onClick={
              handleKeyboardTypeSwitch
                ? () => handleKeyboardTypeSwitch("numeric")
                : handleClick
            }
            className={styles.numericSwitchKey}
          />
          {languageSwitchButton && languageSwitchButton}
        </div>
        <div className={classNames(styles.column, styles.columnTwo)}>
          <KeyboardButton
            dataQA="space"
            type="text"
            text=""
            onClick={() => handleClick({ currentTarget: { name: " " } })}
            className={styles.spaceKey}
            verticalAlign={false}
          />
          <KeyboardButton
            dataQA="search"
            type="icon"
            icon="search"
            value=""
            onClick={showSearchKey ? handleSearch : () => {}}
            className={classNames(styles.searchKey, {
              [styles.searchKeyHidden]: !showSearchKey,
            })}
            disabled={!showSearchKey}
          />
        </div>
      </div>
    </div>
  );
};

export default ElGRKeyboard;
